'use client';

import React, { ReactNode, useMemo } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';

const LOCALES = ['en'];

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function replaceDashesWithSpaces(inputString: string): string {
  return decodePathSegment(inputString)
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
function toLowerCaseWithDashes(inputString: string): string {
  return inputString
    .split(' ')
    .filter(Boolean)
    .map((word) => word.toLowerCase())
    .join('-');
}
function getPathSegments(asPath: string): string[] {
  const segments = asPath
    .split('?')[0]
    .split('#')[0]
    .split('/')
    .filter(Boolean);

  // ✅ Remove locale if it exists as first segment
  if (segments.length > 0 && LOCALES.includes(segments[0])) {
    return segments.slice(1);
  }

  return segments;
}

const getPaths = (pathNames: string[]): string[] => {
  if (pathNames.includes('category')) {
    return pathNames.slice(1);
  } else if (pathNames.includes('catalog')) {
    return pathNames.slice(0, -1);
  } else {
    return pathNames;
  }
};

type BreadCrumbProps = {
  homeElement: ReactNode;
  separator: ReactNode;
  containerClasses?: string;
  listClasses?: string;
  activeClasses?: string;
};

const Breadcrumb = ({
  homeElement,
  separator,
  containerClasses,
  listClasses,
  activeClasses,
}: BreadCrumbProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const asPath = `${pathname}${
    searchParams.toString() ? `?${searchParams.toString()}` : ''
  }`;

  const pathNames = useMemo(() => getPathSegments(asPath), [asPath]);
  const paths = useMemo(() => getPaths(pathNames), [pathNames]);

  const productPath = useMemo(
    () => (pathNames.includes('p') ? pathNames.slice(1) : pathNames),
    [pathNames]
  );

  const isProduct = useMemo(() => pathNames.includes('p'), [pathNames]);

  // Avoid sessionStorage SSR issues
  const menuSelected =
    typeof window !== 'undefined'
      ? sessionStorage.getItem('menuSelected') ?? ''
      : '';

  if (pathNames.length === 0) return null;

  const getHref = (path: string): string => {
    if (isProduct) {
      return `/p/${path}`;
    }

    return `/${path}`;
  };

  return (
    <div>
      <ul className={containerClasses}>
        {/* Home */}
        <li className={listClasses}>
          <Link href="/">{homeElement}</Link>
        </li>

        {paths.length > 0 && separator}

        {paths.map((elem, index) => {
          const path = isProduct
            ? productPath[0]
            : paths.slice(0, index + 1).join('/');

          const href = getHref(path);
          const isLastPath = paths.length === index + 1;

          const itemClasses = `${listClasses} ${
            isLastPath ? activeClasses : ''
          }`;

          const itemLink = replaceDashesWithSpaces(
            elem === 'p' ? menuSelected : elem
          );

          return (
            <React.Fragment key={index}>
              <li className={itemClasses}>
                {isLastPath ? (
                  itemLink
                ) : (
                  <Link
                    href={
                      elem === 'p'
                        ? `/category/${toLowerCaseWithDashes(menuSelected)}`
                        : href
                    }
                  >
                    {itemLink}
                  </Link>
                )}
              </li>

              {paths.length !== index + 1 && separator}
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};

export default Breadcrumb;
